import BigNumber from 'bignumber.js';
import { ITicker, IWalletStats, Network } from '@kattana-io/common';
import TokenBalance from '../../../molecules/TokenBalance';
import {
  formatTimestamp,
  toFixed2,
  toFixed4,
} from '../../DexTrades/formatters';
import HeadTable from '../ModalTable/HeadTable';
import { useStatsTableData } from './useStatsTableData';
import NumberDisplay from '../../../molecules/NumberDisplay';
import { NATIVE_LINK } from '../../../wallet/hooks/useLoadWalletData';
import { NativeCoin } from '../../Trading/helpers/nativeCoins';

interface IStatsTableProps {
  stats: IWalletStats;
  ticker: ITicker;
  base: string;
  quote: string;
  wallet: string;
  volume: number;
  profit: number;
  flipped: boolean;
  timezone?: string;
}

const StatsTable = ({
  stats,
  ticker,
  quote,
  base,
  volume,
  profit,
  wallet,
  flipped,
  timezone,
}: IStatsTableProps) => {
  const [day, time] = formatTimestamp(stats.timestamp, timezone).split(' ');
  const columns = useStatsTableData({
    base: flipped ? quote : base,
    quote,
    balances: !['TRON'].includes(ticker.chain), // skip balances for TRON
  });

  const pnl =
    stats.sell_price_usd.avg && stats.buy_price_usd.avg
      ? `${toFixed4(
        (100 * (stats.sell_price_usd.avg - stats.buy_price_usd.avg)) /
          stats.buy_price_usd.avg
      )}%`
      : '-';
  const vol = volume ? `$${toFixed2(volume)}` : '-';
  const prof = profit ? `$${toFixed2(profit)}` : '-';
  const network = Network.fromChain(ticker.chain);

  const renderColumn = (id: string) => {
    switch (id) {
    case 'firstTrade':
      return (
        <>
          <span>Date: {day}</span>
          <span>Time: {time}</span>
        </>
      );
    case 'type':
      return (
        <>
          <span style={{ color: 'var(--green-600)' }}>
            Buys: {stats.buy_trades_count}
          </span>
          <span style={{ color: 'var(--red-600)' }}>
            Sells: {stats.sell_trades_count}
          </span>
          <span>Bot TX: {stats.bot_trades_count} </span>
        </>
      );
    case 'amount':
      return (
        <>
          <span style={{ color: 'var(--green-600)' }}>
            Buys:{' '}
            <NumberDisplay
              value={Number(stats.amount0_buy)}
              round={2}
              zeroValueDisplay='-'
              string
            />
          </span>
          <span style={{ color: 'var(--red-600)' }}>
            Sells:{' '}
            <NumberDisplay
              value={Number(stats.amount0_sell)}
              round={2}
              zeroValueDisplay='-'
              string
            />
          </span>
        </>
      );
    case 'priceBuyUSD':
      return (
        <>
          <span>
            Max:{' '}
            <NumberDisplay
              value={stats.buy_price_usd.max}
              round={2}
              startSymbol='$'
              zeroValueDisplay='-'
              string
            />
          </span>
          <span>
            Min:{' '}
            <NumberDisplay
              value={stats.buy_price_usd.min}
              round={2}
              startSymbol='$'
              zeroValueDisplay='-'
              string
            />
          </span>
          <span>
            Avg:{' '}
            <span style={{ color: 'var(--green-600)' }}>
              <NumberDisplay
                value={stats.buy_price_usd.avg}
                round={2}
                startSymbol='$'
                zeroValueDisplay='-'
                string
              />
            </span>
          </span>
        </>
      );
    case 'priceBuy':
      return (
        <>
          <span>
            Max:{' '}
            <NumberDisplay
              value={stats.buy_price.max}
              round={2}
              zeroValueDisplay='-'
              string
            />
          </span>
          <span>
            Min:{' '}
            <NumberDisplay
              value={stats.buy_price.min}
              round={2}
              zeroValueDisplay='-'
              string
            />
          </span>
          <span>
            Avg:{' '}
            <NumberDisplay
              value={stats.buy_price.avg}
              round={2}
              zeroValueDisplay='-'
              string
            />
          </span>
        </>
      );
    case 'priceSellUSD':
      return (
        <>
          <span>
            Max:{' '}
            <NumberDisplay
              value={stats.sell_price_usd.max}
              round={2}
              startSymbol='$'
              zeroValueDisplay='-'
              string
            />
          </span>
          <span>
            Min:{' '}
            <NumberDisplay
              value={stats.sell_price_usd.min}
              round={2}
              startSymbol='$'
              zeroValueDisplay='-'
              string
            />
          </span>
          <span>
            Avg:{' '}
            <span style={{ color: 'var(--red-600)' }}>
              <NumberDisplay
                value={stats.sell_price_usd.avg}
                round={2}
                startSymbol='$'
                zeroValueDisplay='-'
                string
              />
            </span>
          </span>
        </>
      );
    case 'priceSell':
      return (
        <>
          <span>
            Max:{' '}
            <NumberDisplay
              value={stats.sell_price.max}
              round={2}
              zeroValueDisplay='-'
              string
            />
          </span>
          <span>
            Min:{' '}
            <NumberDisplay
              value={stats.sell_price.min}
              round={2}
              zeroValueDisplay='-'
              string
            />
          </span>
          <span>
            Avg:{' '}
            <NumberDisplay
              value={stats.sell_price.avg}
              round={2}
              zeroValueDisplay='-'
              string
            />
          </span>
        </>
      );
    case 'total':
      return (
        <>
          <span>Vol: {vol}</span>
          <span>P&L: {pnl}</span>
          <span>Profit: {prof}</span>
        </>
      );
    case 'balances':
      return (
        <>
          {network && (
            <span>
              {network.getNativeCurrencySymbol()}:{' '}
              <TokenBalance
                wallet={wallet}
                network={ticker.chain}
                address={NATIVE_LINK}
                decimals={NativeCoin(network.chain, BigNumber(0)).decimals}
              />
            </span>
          )}
          <span>
            {base}:{' '}
            <TokenBalance
              wallet={wallet}
              network={ticker.chain}
              address={ticker.base_address}
              decimals={ticker.base_decimals}
            />
          </span>
          <span>
            {quote}:{' '}
            <TokenBalance
              wallet={wallet}
              network={ticker.chain}
              address={ticker.quote_address}
              decimals={ticker.quote_decimals}
            />
          </span>
        </>
      );
    default:
      return '';
    }
  };

  return <HeadTable columns={columns} renderColumn={renderColumn} />;
};

export default StatsTable;
